import {
  BookOpenText,
  Gamepad2,
  LogIn,
  LogOut,
  Menu,
  ShieldCheck,
  Trophy,
  UsersRound,
  UserRound,
  X,
} from "lucide-react";
import { useEffect, useState, type ReactNode } from "react";
import { NavLink, useLocation } from "react-router-dom";

import { useAuth } from "../auth/AuthProvider";

const navigation = [
  { to: "/", label: "Ismertető", icon: BookOpenText },
  { to: "/play", label: "Játék", icon: Gamepad2 },
  { to: "/leaderboard", label: "Toplista", icon: Trophy },
  { to: "/about", label: "Rólunk", icon: UsersRound },
];

function navClass({ isActive }: { isActive: boolean }) {
  return isActive ? "nav-link nav-link--active" : "nav-link";
}

export function AppShell({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const auth = useAuth();
  const isGameRoute = pathname === "/play/game";
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (!mobileMenuOpen) {
      return undefined;
    }

    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setMobileMenuOpen(false);
      }
    };

    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [mobileMenuOpen]);

  return (
    <div className={isGameRoute ? "app-shell app-shell--game" : "app-shell"}>
      <a className="skip-link" href="#main-content">
        Ugrás a tartalomhoz
      </a>

      <header className="site-header">
        <div className="site-header__inner">
          <NavLink className="brand" to="/" aria-label="Zsombzilla főoldal">
            <img src="/assets/zsombzilla-logo.png" alt="" className="brand__mark" />
            <span className="brand__copy">
              <strong>Zsombzilla</strong>
              <span>Arcade</span>
            </span>
          </NavLink>

          <nav className="desktop-nav" aria-label="Fő navigáció">
            {navigation.map(({ to, label, icon: Icon }) => (
              <NavLink key={to} className={navClass} to={to} end={to === "/"}>
                <Icon aria-hidden="true" size={18} />
                {label}
              </NavLink>
            ))}
          </nav>

          <details
            className="profile-menu"
            onToggle={(event) => {
              if (event.currentTarget.open) {
                setMobileMenuOpen(false);
              }
            }}
          >
            <summary aria-label="Fiókmenü">
              <UserRound aria-hidden="true" size={20} />
            </summary>
            <div className="profile-menu__panel">
              <div className="profile-menu__status">
                <ShieldCheck aria-hidden="true" size={18} />
                <span>
                  <strong>
                    {auth.status === "loading"
                      ? "Fiók ellenőrzése..."
                      : auth.account?.publicNickname ?? "Nincs bejelentkezve"}
                  </strong>
                  <small>
                    {auth.account?.accountType === "anonymous"
                      ? "Vendégfiók · külön marad a Google-fióktól"
                      : auth.account?.accountType === "google"
                        ? "Google-fiók · csak a választott becenév nyilvános"
                        : auth.status === "unconfigured"
                          ? "A Firebase konfiguráció nincs megadva ebben a buildben."
                          : "Válassz vendégjátékot vagy Google-belépést."}
                  </small>
                </span>
              </div>
              {auth.account ? (
                <button
                  type="button"
                  className="profile-menu__action"
                  disabled={auth.action !== "idle"}
                  onClick={() => void auth.signOut()}
                >
                  <LogOut aria-hidden="true" size={17} />
                  Kijelentkezés
                </button>
              ) : (
                <NavLink to="/play" className="profile-menu__action">
                  <LogIn aria-hidden="true" size={17} />
                  Játékmód választása
                </NavLink>
              )}
            </div>
          </details>

          <div className="mobile-menu">
            <button
              type="button"
              className="mobile-menu__toggle"
              aria-controls="mobile-navigation"
              aria-expanded={mobileMenuOpen}
              aria-label={mobileMenuOpen ? "Menü bezárása" : "Menü megnyitása"}
              onClick={() => setMobileMenuOpen((open) => !open)}
            >
              {mobileMenuOpen ? (
                <X aria-hidden="true" size={22} />
              ) : (
                <Menu aria-hidden="true" size={22} />
              )}
            </button>

            {mobileMenuOpen && (
              <>
                <button
                  type="button"
                  className="mobile-menu__backdrop"
                  aria-label="Mobil menü bezárása"
                  onClick={() => setMobileMenuOpen(false)}
                />
                <nav id="mobile-navigation" className="mobile-menu__panel" aria-label="Mobil navigáció">
                  {navigation.map(({ to, label, icon: Icon }) => (
                    <NavLink
                      key={to}
                      className={navClass}
                      to={to}
                      end={to === "/"}
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <Icon aria-hidden="true" size={20} />
                      {label}
                    </NavLink>
                  ))}
                </nav>
              </>
            )}
          </div>
        </div>
      </header>

      <main id="main-content" className="site-main" tabIndex={-1}>
        {children}
      </main>

      {!isGameRoute && (
        <footer className="site-footer">
          <div className="content-frame site-footer__inner">
            <span>ZSOMBZILLA</span>
            <div className="site-footer__links">
              <NavLink to="/about">Rólunk</NavLink>
              <NavLink to="/privacy">Adatkezelés</NavLink>
            </div>
          </div>
        </footer>
      )}
    </div>
  );
}
